import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { getRoute } from 'js/constants/routes';
import PropTypes from 'prop-types';
import UrlParamsExtractor from 'js/helpers/search/UrlParamsExtractor';
import RapidSearch from 'js/constants/rapidSearch';
import { SUGGESTION_CONTEXT } from 'js/constants/suggestionTypes';
import {
    createEventSuggestion,
    trackCompanySearch,
    trackEvent,
    trackNameSearch,
    trackVolumeFolioSearch,
} from 'js/actions/segment';
import RapidConstructOwnerSearchEvent from 'js/helpers/nameSearch/RapidConstructOwnerSearchEvent';
import SEGMENT from '../constants/segment';
import ListReportPreview from 'js/constants/listPropertyPreview';
import Segment from 'js/helpers/Segment';

const useRefineSearchEventTracker = (props) => {
    const dispatch = useDispatch();
    const location = useLocation();
   const history = useHistory();
    const activeView = useSelector(state => state.searchResults.get('activeView'));

    const pageContext = getRoute(location.pathname).pageContext;

    const logFilters = () => {
        const _filters = props.filters.getFlatFilters({}, props.statusType);
        props.logFilters({
            ..._filters,
        }, props.statusType);
    }

    // update route state so it won't trigger another event after reload with the same URL
    // Everytime the route state.entryPoint has a value, mixpanel event won
    const hydrateRouteState = () => {
        history.replace({
            pathname: location.pathname,
            search: location.search,
            state: { entryPoint: SEGMENT.EVENT_PROPERTIES.DIRECT_URL },
        });
    };

    const addressLogger = () => {
        const paramsObject = UrlParamsExtractor.parseUrlParams(location.search, props.searchType);
        const { sType, suggestions, active, statusType } = paramsObject || {};

        dispatch(createEventSuggestion(
            pageContext,
            SEGMENT.EVENT_PROPERTIES.DIRECT_URL,
            SUGGESTION_CONTEXT?.[sType],
            suggestions?.split('|'),
            SEGMENT.EVENT_PROPERTIES.ADDRESS,
            undefined,
            !active,
            RapidSearch.TABS_ATTR[statusType],
            activeView,
        ));
    };

    const nameLogger = () => {
        const {
            isCurrentOwner,
            exactSearch,
            ownerFirstName,
            ownerLastName,
            addressSuburb,
            addressState,
        } = UrlParamsExtractor
            .parseUrlParams(location?.search, props.searchType) || {};
        dispatch(trackNameSearch(RapidConstructOwnerSearchEvent(
            {
                ownerFirstName, ownerLastName, addressState, addressSuburb,
            },
            pageContext,
            SEGMENT.EVENT_PROPERTIES.DIRECT_URL,
            isCurrentOwner,
            SEGMENT.EVENT_PROPERTIES.NAME,
            exactSearch,
            activeView,
        )));
    }

    const companyLogger = () => {
        const {
            companyName, isCurrentOwner, addressSuburb, addressState, exactSearch,
        } = UrlParamsExtractor
            .parseUrlParams(location?.search, props.searchType) || {};
        dispatch(trackCompanySearch({
            pageContext,
            searchString: `${companyName}${addressState}${addressSuburb}`,
            entryPoint: SEGMENT.EVENT_PROPERTIES.TYPED,
            currentOwner: isCurrentOwner,
            searchType: SEGMENT.EVENT_PROPERTIES.COMPANY,
            exactSearch,
            activeView,
        }));
    };

    const buildingLogger = () => {
        const {
            addressBuildingName, exactSearch, addressSuburb, addressState,
        } = UrlParamsExtractor
            .parseUrlParams(location?.search, props.searchType) || {};
        dispatch(trackEvent(Segment.buildingSearch({
            pageContext,
            exactSearch: !!exactSearch,
            searchString: `${addressBuildingName}, ${addressState}${addressSuburb || ''}`,
            activeView,
            entryPoint: SEGMENT.EVENT_PROPERTIES.DIRECT_URL,
        })));
    };

    const parcelLogger = () => {
        const searchParamKey = RapidSearch.BASE_FILTER_BY_SEARCH_TYPE[props.searchType];
        const {
            [searchParamKey]: dynamicKey, statusType,
        } = UrlParamsExtractor
            .parseUrlParams(location?.search, props.searchType) || {};

        dispatch(createEventSuggestion(
            pageContext,
            SEGMENT.EVENT_PROPERTIES.DIRECT_URL,
            ListReportPreview.MIXPANEL_SEARCH_TYPE[props.searchType],
            dynamicKey,
            ListReportPreview.MIXPANEL_SEARCH_TYPE[props.searchType],
            false,
            false,
            RapidSearch.TABS_ATTR[statusType],
            activeView,
        ));
    };

    const volumeFolioLogger = () => {
        const {
            folioNumber, volumeNumber, addressState, addressSuburb,
        } = UrlParamsExtractor
            .parseUrlParams(location?.search, props.searchType) || {};

        dispatch(trackVolumeFolioSearch({
            pageContext,
            searchString: [volumeNumber, folioNumber, addressState, addressSuburb]
                .filter(Boolean)
                .join(', '),
            entryPoint: SEGMENT.EVENT_PROPERTIES.DIRECT_URL,
            searchType: SEGMENT.EVENT_PROPERTIES.VOLUME_FOLIO,
            activeView,
        }));
    };
    const rollValuationLogger = () => {
        const {
            valuationReference, addressSuburb,
        } = UrlParamsExtractor
            .parseUrlParams(location?.search, props.searchType) || {};
        dispatch(trackEvent(Segment.trackRollValuationSearch({
            pageContext,
            searchString: [valuationReference, addressSuburb]
                .filter(Boolean)
                .join(', '),
            entryPoint: SEGMENT.EVENT_PROPERTIES.DIRECT_URL,
            searchType: SEGMENT.EVENT_PROPERTIES.ROLL_VALUATION,
            activeView,
        })));
    };

    const logger = {
        [ListReportPreview.SEARCH_TYPE.ADDRESS]: addressLogger,
        [ListReportPreview.SEARCH_TYPE.NAME]: nameLogger,
        [ListReportPreview.SEARCH_TYPE.COMPANY]: companyLogger,
        [ListReportPreview.SEARCH_TYPE.BUILDING]: buildingLogger,
        [ListReportPreview.SEARCH_TYPE.PARCEL]: parcelLogger,
        [ListReportPreview.SEARCH_TYPE.LEGAL_DESCRIPTION]: parcelLogger,
        [ListReportPreview.SEARCH_TYPE.TITLE_REFERENCE]: parcelLogger,
        [ListReportPreview.SEARCH_TYPE.VOLUME_FOLIO]: volumeFolioLogger,
        [ListReportPreview.SEARCH_TYPE.ROLL_VALUATION]: rollValuationLogger,
    };

    const logBaseFilters = () => {
        const mixpanelEvent = logger[props.searchType];
        if (mixpanelEvent) {
            mixpanelEvent();
        }
        hydrateRouteState();
    }

    // this for direct url changes
    // only direct url changes can make the initialized true and
    // isResetDisabled false (if there are filters in the url)
    // if we use the searchbar, only initialized will be true and
    // isResetDisabled will be true since search bar cannot apply any immediate filters
    useEffect(() => {
        if (props.shouldTrack) {
            if (props.initialized
                && !props.filters.isResetDisabled
                && !location?.state?.entryPoint) {
                logFilters();
            }

            // empty/null/undefined location?.state?.entryPoint means
            // that the search is from a direct URL
            if (props.initialized && !location?.state?.entryPoint) {
                logBaseFilters();
            }
        }
    }, [props.initialized, props.shouldTrack]);
};

useRefineSearchEventTracker.propTypes = {
    initialized: PropTypes.bool,
    filters: PropTypes.object,
    searchType: PropTypes.string,
    shouldTrack: PropTypes.bool,
};

export default useRefineSearchEventTracker;
